/**
 * Keys — view and rotate the platform's API keys (T-143).
 *
 * ## What this screen may and may not show
 *
 * **It never shows a key.** Not after saving, not on reload, not once. The
 * server's view type has no field a value could arrive in, so there is nothing
 * here to render even by mistake; what the operator gets is the last four
 * characters, a fingerprint, who set it and when. That is enough to answer
 * "which key is live" and not enough to be worth stealing.
 *
 * There is deliberately no reveal, not even a reveal-once. Every key on this
 * screen is one an operator pasted in from somewhere else, so they already have
 * it; a reveal would add a way to lose it and no way to gain anything.
 *
 * ## The state that matters most
 *
 * A key can be **stored but unproven** — `validation: 'unavailable'` — which
 * happens when the provider could not be reached at the moment it was saved.
 * That is not a pass and must not look like one. ADR-030 exists because a wrong
 * provider key read as a quiet, healthy chain for as long as nobody noticed
 * deposits had stopped, so this screen treats "we could not check" as its own
 * colour and offers a re-check rather than letting it settle into the
 * background.
 *
 * A refused rotation is the other one. When the pre-activation check comes back
 * `misconfigured` the server writes nothing, and the toast says so in those
 * words — **the old key is still live** — because an operator who thinks they
 * have just broken the deposit rail behaves very differently from one who knows
 * they have not.
 */
import { useState, type ReactNode } from 'react';
import {
  checkSecret,
  fetchSecretHistory,
  fetchSecrets,
  revokeSecret,
  setSecret,
} from '../api/client.ts';
import { ApiError } from '../api/http.ts';
import type { SecretValidation, SecretVersion, SecretView } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { age } from '../lib/datetime.ts';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import {
  IconAlert,
  IconCheck,
  IconClock,
  IconLock,
  IconPulse,
  IconRefresh,
  IconShield,
} from '../ui/icons.tsx';

const COLUMNS = ['text', 'text', 'text', 'text', 'text'] as const;

/** Where the live value comes from, in words an operator can act on. */
function sourceLabel(secret: SecretView): { text: string; tone: 'win' | 'neutral' | 'loss' } {
  if (secret.source === 'database') return { text: `stored · v${secret.version}`, tone: 'win' };
  if (secret.source === 'environment') return { text: 'environment', tone: 'neutral' };
  return { text: 'not set', tone: 'loss' };
}

/**
 * The verdict, and its colour. `unavailable` is deliberately NOT green: the key
 * was stored without being proven, and a green tick would be a claim nobody
 * made.
 */
function verdict(secret: SecretView): { text: string; tone: 'win' | 'neutral' | 'loss' } | null {
  if (secret.source !== 'database' || !secret.validation) return null;
  switch (secret.validation) {
    case 'ok':
      return { text: 'checked', tone: 'win' };
    case 'unavailable':
      return { text: 'unproven', tone: 'loss' };
    default:
      return { text: 'not checkable', tone: 'neutral' };
  }
}

export function Secrets(): ReactNode {
  const secrets = useAsync(() => fetchSecrets(), []);
  const [selected, setSelected] = useState<string | null>(null);

  const rows = secrets.data ?? [];
  const active = rows.find((s) => s.name === selected) ?? null;
  const missingRequired = rows.filter((s) => s.required && !s.configured);

  return (
    <div className="split">
      <div className="stack">
        <div className="toolbar">
          <span className="toolbar__note">
            Keys are stored encrypted and are never shown again — only the last four characters. A
            new key is tried against its provider before it goes live, so a broken key is refused
            rather than quietly stopping the rail.
          </span>
          <button type="button" className="btn btn--sm btn--ghost" onClick={secrets.reload}>
            <IconRefresh size="sm" />
            Refresh
          </button>
        </div>

        {missingRequired.length > 0 ? (
          <Card title="A required key is missing" icon={<IconAlert />}>
            <p className="field__hint field__hint--block">
              {missingRequired.map((s) => s.label).join(', ')} — the rail this key serves cannot
              work without it, and it fails <em>silently</em>: the chain reads as quiet rather than
              broken, and deposits simply never credit.
            </p>
          </Card>
        ) : null}

        <Card title="Platform keys" icon={<IconShield />} count={rows.length} flush>
          {secrets.error && rows.length === 0 ? (
            <ErrorState
              title="The key manager did not load"
              detail={secrets.error}
              onRetry={secrets.reload}
            />
          ) : secrets.loading && rows.length === 0 ? (
            <TableSkeleton columns={COLUMNS} rows={6} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<IconShield size="lg" />}
              title="No keys are managed"
              hint="The registry is a list in the server. Adding a key is a code change, not a row an operator can invent."
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Source</th>
                    <th>Last 4</th>
                    <th>Checked</th>
                    <th>Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const source = sourceLabel(row);
                    const state = verdict(row);
                    return (
                      <tr
                        key={row.name}
                        className={`row--clickable${row.name === selected ? ' is-selected' : ''}`}
                        onClick={() => setSelected(row.name)}
                      >
                        <td className="nowrap cell-lead">
                          {row.label}
                          {row.required ? ' *' : ''}
                          <span className="dim mono block">{row.name}</span>
                        </td>
                        <td>
                          <Badge tone={source.tone}>{source.text}</Badge>
                        </td>
                        <td className="mono dim">{row.last4 ? `…${row.last4}` : '—'}</td>
                        <td>{state ? <Badge tone={state.tone}>{state.text}</Badge> : <span className="dim">—</span>}</td>
                        <td className="dim nowrap">{row.setAt ? age(row.setAt) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <SecretPanel secret={active} onChanged={secrets.reload} />
    </div>
  );
}

function SecretPanel({
  secret,
  onChanged,
}: {
  secret: SecretView | null;
  onChanged: () => void;
}): ReactNode {
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [history, setHistory] = useState<SecretVersion[] | null>(null);
  const [lastCheck, setLastCheck] = useState<SecretValidation | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  if (!secret) {
    return (
      <Card title="Key" icon={<IconLock />}>
        <EmptyState
          compact
          icon={<IconLock size="lg" />}
          title="No key selected"
          hint="Pick a row to see where its value comes from, when it last changed, and to rotate it."
        />
      </Card>
    );
  }

  // The selected name is the identity of the form. Reset the draft when it
  // changes, without an effect — and never carry a typed value across keys.
  if (editing !== secret.name) {
    setEditing(secret.name);
    setValue('');
    setNote('');
    setHistory(null);
    setLastCheck(null);
  }

  async function onSave(): Promise<void> {
    if (!secret || value.trim().length === 0) return;
    const result = await confirm({
      title: secret.configured ? 'Rotate this key' : 'Set this key',
      body:
        'The new value is tried against its provider before anything is stored. If the provider refuses it, nothing is written and the current key keeps working.',
      confirmLabel: secret.configured ? 'Rotate' : 'Set',
      facts: [
        { label: 'Key', value: secret.name },
        { label: 'Currently', value: secret.last4 ? `…${secret.last4} (${secret.source})` : 'not set' },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const saved = await setSecret(secret.name, value.trim(), note.trim() || result.note);
      setValue('');
      setNote('');
      setHistory(null);
      setLastCheck(saved.validation);
      toast(
        saved.validation.state === 'ok'
          ? `${secret.label} rotated to v${saved.secret.version}. The provider answered.`
          : `${secret.label} stored as v${saved.secret.version}, but it could NOT be checked — treat it as unproven.`,
        saved.validation.state === 'ok' ? 'ok' : 'error',
      );
      onChanged();
    } catch (cause) {
      // The refusal case, spelled out. An operator who believes they have just
      // broken the deposit rail behaves very differently from one who knows the
      // old key is untouched.
      if (cause instanceof ApiError && cause.code === 'secret_rejected') {
        toast(`Refused, and nothing was changed — ${cause.message}`, 'error');
      } else {
        toast(cause instanceof Error ? cause.message : 'Could not store the key.', 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onCheck(): Promise<void> {
    if (!secret) return;
    setBusy(true);
    try {
      const result = await checkSecret(secret.name);
      setLastCheck(result);
      toast(
        result.state === 'ok' ? 'The provider answered with the live key.' : result.detail,
        result.state === 'ok' ? 'ok' : 'error',
      );
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not run the check.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(): Promise<void> {
    if (!secret) return;
    const result = await confirm({
      title: 'Remove this key from the store',
      intent: 'danger',
      body:
        'The stored value stops being used and this key falls back to the environment variable of the same name. If that variable is not set, the rail this key serves stops working. Nothing is deleted — the history stays.',
      confirmLabel: 'Remove from store',
      facts: [
        { label: 'Key', value: secret.name },
        { label: 'Falls back to', value: `the ${secret.name} environment variable` },
      ],
      note: { label: 'Reason', required: true },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const view = await revokeSecret(secret.name, result.note);
      setHistory(null);
      toast(
        view.configured
          ? `${secret.label} now resolves from the environment.`
          : `${secret.label} is now unset — the environment does not supply it either.`,
        view.configured ? 'ok' : 'error',
      );
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not remove the key.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onLoadHistory(): Promise<void> {
    if (!secret) return;
    try {
      setHistory(await fetchSecretHistory(secret.name));
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not load the history.', 'error');
    }
  }

  const state = verdict(secret);

  return (
    <div className="stack">
      <Card title={secret.label} icon={<IconLock />}>
        <p className="field__hint field__hint--block">{secret.description}</p>

        <div className="kv kv--spaced">
          <span className="kv__k">Variable</span>
          <span className="kv__v num">{secret.name}</span>
          <span className="kv__k">Source</span>
          <span className="kv__v">{sourceLabel(secret).text}</span>
          <span className="kv__k">Last 4</span>
          <span className="kv__v num">{secret.last4 ? `…${secret.last4}` : '—'}</span>
          {secret.fingerprint ? (
            <>
              <span className="kv__k">Fingerprint</span>
              <span className="kv__v num">{secret.fingerprint}</span>
            </>
          ) : null}
          {secret.setBy ? (
            <>
              <span className="kv__k">Set by</span>
              <span className="kv__v">
                {secret.setBy} · {secret.setAt ? age(secret.setAt) : ''}
              </span>
            </>
          ) : null}
          <span className="kv__k">Versions</span>
          <span className="kv__v">{secret.versions}</span>
        </div>

        {state ? (
          <p className="field__hint field__hint--block">
            <Badge tone={state.tone}>{state.text}</Badge>{' '}
            {lastCheck?.detail ?? secret.validationDetail}
          </p>
        ) : null}

        <label className="field">
          <span className="field__label">
            {secret.configured ? 'New value' : 'Value'} — pasted once, never shown again
          </span>
          <input
            className="input num"
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder={secret.configured ? `replaces …${secret.last4 ?? ''}` : 'paste the key'}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <span className="field__hint">
            Paste the key on its own. A trailing space or newline is refused rather than silently
            stripped — storing something you did not type is how a key fails at 4am.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Note</span>
          <input
            className="input"
            value={note}
            placeholder="why this changed"
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        <div className="row-actions row-actions--start">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy || value.trim().length === 0}
            onClick={onSave}
          >
            <IconCheck size="sm" />
            {secret.configured ? 'Rotate key' : 'Set key'}
          </button>
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            disabled={busy || !secret.configured || !secret.canValidate}
            onClick={onCheck}
          >
            <IconPulse size="sm" />
            Check the live key
          </button>
          <button
            type="button"
            className="btn btn--sm btn--danger"
            disabled={busy || secret.source !== 'database'}
            onClick={onRevoke}
          >
            Remove from store
          </button>
        </div>
      </Card>

      <Card
        title="History"
        icon={<IconClock />}
        actions={
          <button type="button" className="btn btn--sm btn--ghost" onClick={onLoadHistory}>
            <IconRefresh size="sm" />
            Load
          </button>
        }
      >
        <p className="field__hint field__hint--block">
          Append-only. A rotation writes a new version; nothing overwrites or deletes one. This is
          what answers &ldquo;did a key change just before deposits stopped?&rdquo;
        </p>
        {history === null ? (
          <EmptyState compact icon={<IconClock size="lg" />} title="Not loaded" hint="Load the versions for this key." />
        ) : history.length === 0 ? (
          <EmptyState compact icon={<IconClock size="lg" />} title="No versions" hint="This key has never been stored — it comes from the environment, or from nowhere." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>v</th>
                  <th>Last 4</th>
                  <th>State</th>
                  <th>By</th>
                  <th>When</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.version}>
                    <td className="mono">{row.version}</td>
                    <td className="mono dim">{row.last4 ? `…${row.last4}` : '—'}</td>
                    <td>
                      {row.revoked ? (
                        <Badge tone="neutral">revoked</Badge>
                      ) : row.live ? (
                        <Badge tone="win">live</Badge>
                      ) : (
                        <Badge tone="neutral">superseded</Badge>
                      )}
                    </td>
                    <td className="dim">{row.setBy ?? '—'}</td>
                    <td className="dim nowrap">{age(row.setAt)}</td>
                    <td className="dim">{row.note ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
